import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

export default function CierreSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="cierre" ref={ref} className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-16">

        {/* Main CTA Card */}
        <div
          className={`rounded-3xl overflow-hidden transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{
            background: "linear-gradient(135deg, #001A3D 0%, #002D62 60%, #003580 100%)",
            border: "1px solid rgba(212,175,55,0.3)"
          }}
        >
          {/* Top gold line */}
          <div className="h-1 w-full" style={{ background: "linear-gradient(90deg, transparent, #D4AF37, transparent)" }} />

          <div className="px-8 md:px-20 py-16 md:py-24 text-center">

            {/* Icon */}
            <div
              className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-8 transition-all duration-1000 ${visible ? "opacity-100 scale-100" : "opacity-0 scale-75"}`}
              style={{
                background: "rgba(212,175,55,0.15)",
                border: "2px solid rgba(212,175,55,0.5)",
                transitionDelay: "0.3s"
              }}
            >
              <i className="ri-book-open-line text-3xl" style={{ color: "#D4AF37" }} />
            </div>

            {/* Main Message */}
            <h2
              className={`font-bold mb-6 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
                color: "#FFFFFF",
                lineHeight: 1.25,
                transitionDelay: "0.4s"
              }}
            >
              No es solo una historia de supervivencia
            </h2>

            <p
              className={`max-w-2xl mx-auto mb-12 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.1rem, 2vw, 1.4rem)",
                color: "rgba(255,255,255,0.85)",
                fontStyle: "italic",
                lineHeight: 1.7,
                transitionDelay: "0.5s"
              }}
            >
              Es una invitación a encontrar esperanza en tu propia adversidad.
            </p>

            {/* CTA Button */}
            <div
              className={`transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
              style={{ transitionDelay: "0.6s" }}
            >
              <a
                href="https://books2read.com/el-secuestro-de-lucho"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{
                  background: "linear-gradient(135deg, #D4AF37, #F0D060)",
                  color: "#002D62",
                  padding: "20px 48px",
                  borderRadius: "50px",
                  fontSize: "1.05rem",
                  boxShadow: "0 8px 40px rgba(212,175,55,0.4)"
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-4px)";
                  (e.currentTarget as HTMLAnchorElement).style.boxShadow = "0 20px 60px rgba(212,175,55,0.55)";
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)";
                  (e.currentTarget as HTMLAnchorElement).style.boxShadow = "0 8px 40px rgba(212,175,55,0.4)";
                }}
              >
                <i className="ri-amazon-fill text-xl" />
                Adquirir en Amazon
              </a>
            </div>

            {/* Divider */}
            <div className="flex items-center justify-center gap-4 mt-14 mb-6">
              <div className="h-px w-24" style={{ background: "rgba(212,175,55,0.4)" }} />
              <i className="ri-mail-line text-sm" style={{ color: "rgba(212,175,55,0.6)" }} />
              <div className="h-px w-24" style={{ background: "rgba(212,175,55,0.4)" }} />
            </div>

            {/* Contact */}
            <a
              href="mailto:carlos.diaz0163@gmail.com"
              className="inline-flex items-center gap-2 transition-all duration-300 cursor-pointer"
              style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.95rem" }}
              onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.color = "#D4AF37"}
              onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.color = "rgba(255,255,255,0.6)"}
            >
              <i className="ri-mail-send-line" style={{ color: "#D4AF37" }} />
              carlos.diaz0163@gmail.com
            </a>
          </div>

          {/* Bottom gold line */}
          <div className="h-1 w-full" style={{ background: "linear-gradient(90deg, transparent, #D4AF37, transparent)" }} />
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-16 text-center px-6">
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="h-px w-20" style={{ background: "rgba(0,45,98,0.2)" }} />
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#D4AF37" }} />
          <div className="h-px w-20" style={{ background: "rgba(0,45,98,0.2)" }} />
        </div>
        <p className="text-sm" style={{ color: "#999", fontFamily: "'Inter', sans-serif" }}>
          &copy; {new Date().getFullYear()} El Secuestro de Lucho &mdash; Carlos Alberto Díaz Martínez. Todos los derechos reservados.
        </p>
        <p className="text-xs mt-2" style={{ color: "#bbb" }}>
          <a href="mailto:carlos.diaz0163@gmail.com" className="hover:underline cursor-pointer" style={{ color: "#D4AF37" }}>
            carlos.diaz0163@gmail.com
          </a>
        </p>
        <div className="flex items-center justify-center gap-2 mt-4">
          <div className="h-px w-8" style={{ background: "rgba(212,175,55,0.3)" }} />
          <Link
            to="/politicas"
            className="inline-flex items-center gap-1.5 text-xs transition-all duration-200 cursor-pointer whitespace-nowrap"
            style={{ color: "#bbb" }}
            onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.color = "#D4AF37"}
            onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.color = "#bbb"}
          >
            <i className="ri-shield-check-line text-xs" />
            Políticas y Privacidad
          </Link>
          <div className="h-px w-8" style={{ background: "rgba(212,175,55,0.3)" }} />
        </div>
      </footer>
    </section>
  );
}
